import { useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { DocumentPreview } from '@/components/DocumentPreview';
import { Badge, Button, Card, TextAreaField } from '@/components/ui';
import { documentStatusTone, registrationStatusTone } from '@/lib/adminTones';
import { formatDate, formatDateTime } from '@/lib/dates';
import { formatPrice } from '@/lib/i18n';
import { useAddAdminNote, useAdminRegistration } from '@/lib/queries';

function Row({ label, children }: { label: ReactNode; children: ReactNode }) {
  return (
    <div className="flex justify-between gap-4 text-sm">
      <dt className="text-ink-muted">{label}</dt>
      <dd className="text-right font-semibold break-words">{children}</dd>
    </div>
  );
}

/** A-04: one registration with the person's profile, orders, invitations, documents and notes. */
export function AdminRegistration() {
  const { t } = useTranslation();
  const { id } = useParams();
  const reg = useAdminRegistration(id);
  const addNote = useAddAdminNote(id ?? '');
  const [note, setNote] = useState('');
  const [preview, setPreview] = useState<string | null>(null);

  if (reg.isLoading) return <p className="text-center text-ink-muted">…</p>;
  if (reg.isError || !reg.data) {
    return (
      <section className="space-y-4">
        <Link to="/admin/registrations">{t('admin.detail.back')}</Link>
        <p role="alert" className="text-danger">
          {t('admin.detail.error')}
        </p>
      </section>
    );
  }
  const r = reg.data;
  const p = r.profile;
  const none = t('admin.none');
  const name = [p.firstName, p.lastName].filter(Boolean).join(' ') || t('admin.unnamed');

  const submitNote = async () => {
    const body = note.trim();
    if (!body) return;
    try {
      await addNote.mutateAsync({ body });
      setNote('');
    } catch {
      // Shown through addNote.isError.
    }
  };

  return (
    <section className="space-y-6">
      <Link to="/admin/registrations" className="text-sm">
        {t('admin.detail.back')}
      </Link>
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="font-display text-section-lg font-bold">{name}</h1>
        <Badge tone={registrationStatusTone(r.status)}>{t(`admin.status.${r.status}`)}</Badge>
        <Badge tone={documentStatusTone(r.documentStatus)}>
          {t(`admin.documents.${r.documentStatus}`)}
        </Badge>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <div className="space-y-6">
          <Card>
            <h2 className="text-sm font-bold">{t('admin.detail.summary')}</h2>
            <dl className="space-y-1.5">
              <Row label={t('admin.col.format')}>{r.format.name}</Row>
              <Row label={t('admin.detail.tier')}>{r.pricingTierName ?? none}</Row>
              <Row label={t('admin.detail.price')}>{formatPrice(r.priceCents)}</Row>
              <Row label={t('admin.detail.bib')}>{r.bibNumber ?? none}</Row>
              <Row label={t('admin.detail.registeredAt')}>{formatDateTime(r.createdAt)}</Row>
            </dl>
          </Card>

          <Card>
            <h2 className="text-sm font-bold">{t('admin.person.title')}</h2>
            <dl className="space-y-1.5">
              <Row label={t('admin.col.email')}>{p.email}</Row>
              <Row label={t('admin.person.birthDate')}>{p.birthDate ?? none}</Row>
              <Row label={t('admin.person.gender')}>{p.gender ?? none}</Row>
              <Row label={t('admin.person.phone')}>{p.phone ?? none}</Row>
              <Row label={t('admin.person.nationality')}>{p.nationality ?? none}</Row>
              <Row label={t('admin.person.club')}>{p.clubName ?? none}</Row>
              <Row label={t('admin.person.emergency')}>
                {[p.emergencyContactName, p.emergencyContactPhone, p.emergencyContactRelationship]
                  .filter(Boolean)
                  .join(' · ') || none}
              </Row>
              <Row label={t('admin.person.licence')}>
                {p.licence ? (
                  <>
                    {p.licence.federation} · {p.licence.number}{' '}
                    <Badge
                      tone={
                        p.licence.status === 'approved'
                          ? 'success'
                          : p.licence.status === 'rejected'
                            ? 'danger'
                            : 'accent'
                      }
                    >
                      {t(`admin.docStatus.${p.licence.status}`)}
                    </Badge>
                  </>
                ) : (
                  none
                )}
              </Row>
              <Row label={t('admin.person.language')}>{p.locale.toUpperCase()}</Row>
              <Row label={t('admin.person.marketing')}>
                {t(p.marketingOptIn ? 'admin.person.yes' : 'admin.person.no')}
              </Row>
            </dl>
          </Card>

          <Card>
            <h2 className="text-sm font-bold">{t('admin.docs.title')}</h2>
            {r.documents.length === 0 ? (
              <p className="text-sm text-ink-muted">{t('admin.docs.none')}</p>
            ) : (
              <ul className="space-y-2">
                {r.documents.map((d) => (
                  <li
                    key={d.id}
                    className="flex flex-wrap items-center justify-between gap-3 text-sm"
                  >
                    <span>
                      {t(`admin.docKind.${d.kind}`)}
                      <span className="text-ink-muted">
                        {' · '}
                        {t('admin.docs.uploadedAt', { date: formatDateTime(d.uploadedAt) })}
                        {d.expiresOn &&
                          ` · ${t('admin.docs.expiresOn', { date: formatDate(`${d.expiresOn}T12:00:00Z`) })}`}
                      </span>
                      {d.rejectionReason && (
                        <span className="block text-helper text-danger">{d.rejectionReason}</span>
                      )}
                    </span>
                    <Badge
                      tone={
                        d.status === 'approved'
                          ? 'success'
                          : d.status === 'rejected'
                            ? 'danger'
                            : 'accent'
                      }
                    >
                      {t(`admin.docStatus.${d.status}`)}
                    </Badge>
                    <span className="flex w-full gap-4">
                      <button
                        type="button"
                        className="text-sm font-semibold text-accent"
                        aria-expanded={preview === d.id}
                        onClick={() => setPreview((v) => (v === d.id ? null : d.id))}
                      >
                        {t(preview === d.id ? 'admin.docs.hidePreview' : 'admin.docs.preview')}
                      </button>
                      {d.status === 'pending_review' && (
                        <Link
                          to={`/admin/documents?item=certificate:${d.id}`}
                          className="text-sm font-semibold"
                        >
                          {t('admin.docs.review')}
                        </Link>
                      )}
                    </span>
                    {preview === d.id && (
                      <div className="w-full">
                        <DocumentPreview documentId={d.id} fileName={d.fileName} />
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>

        <div className="space-y-6">
          <Card>
            <h2 className="text-sm font-bold">{t('admin.orders.title')}</h2>
            <ul className="space-y-4">
              {r.orders.map((o) => (
                <li
                  key={o.id}
                  className="space-y-1.5 border-t border-line pt-3 first:border-0 first:pt-0"
                >
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge
                      tone={registrationStatusTone(o.status === 'failed' ? 'failed' : o.status)}
                    >
                      {t(`admin.orderStatus.${o.status}`)}
                    </Badge>
                    {o.insured && <Badge tone="success">{t('admin.insured.yes')}</Badge>}
                    <span className="font-semibold">
                      {t('admin.orders.total', { amount: formatPrice(o.totalCents) })}
                    </span>
                    {o.discountCents > 0 && (
                      <span className="text-sm text-ink-muted">
                        {t('admin.orders.discount', { amount: formatPrice(o.discountCents) })}
                        {o.voucherCode &&
                          ` · ${t('admin.orders.voucher', { code: o.voucherCode })}`}
                      </span>
                    )}
                  </div>
                  <ul className="text-sm text-ink-muted">
                    {o.registrations.map((x) => (
                      <li key={x.id}>
                        {x.formatName} · {formatPrice(x.priceCents)}
                      </li>
                    ))}
                    {o.insured && (
                      <li>
                        {t('admin.orders.insurance')} · {formatPrice(o.insuranceCents)}
                      </li>
                    )}
                  </ul>
                  <div className="flex flex-wrap gap-x-4 text-sm">
                    {o.paidAt && (
                      <span className="text-ink-muted">
                        {t('admin.orders.paidAt', { date: formatDateTime(o.paidAt) })}
                      </span>
                    )}
                    {o.stripeUrl && (
                      <a href={o.stripeUrl} target="_blank" rel="noreferrer">
                        {t('admin.orders.stripe')}
                      </a>
                    )}
                    {o.receiptUrl && (
                      <a href={o.receiptUrl} target="_blank" rel="noreferrer">
                        {t('admin.orders.receipt')}
                      </a>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </Card>

          <Card>
            <h2 className="text-sm font-bold">{t('admin.invites.title')}</h2>
            <h3 className="text-sm font-semibold text-ink-muted">{t('admin.invites.sent')}</h3>
            {r.invitationsSent.length === 0 ? (
              <p className="text-sm text-ink-muted">{t('admin.invites.none')}</p>
            ) : (
              <ul className="space-y-1 text-sm">
                {r.invitationsSent.map((i) => (
                  <li key={i.id} className="flex justify-between gap-3">
                    <span>{i.email}</span>
                    <Badge tone={i.status === 'registered' ? 'success' : 'neutral'}>
                      {t(`admin.inviteStatus.${i.status}`)}
                    </Badge>
                  </li>
                ))}
              </ul>
            )}
            <h3 className="mt-2 text-sm font-semibold text-ink-muted">
              {t('admin.invites.received')}
            </h3>
            {r.invitationsReceived.length === 0 ? (
              <p className="text-sm text-ink-muted">{t('admin.invites.none')}</p>
            ) : (
              <ul className="space-y-1 text-sm">
                {r.invitationsReceived.map((i) => (
                  <li key={i.id} className="flex justify-between gap-3">
                    <span>
                      {i.referrerEmail
                        ? t('admin.invites.from', { email: i.referrerEmail })
                        : i.email}
                    </span>
                    <Badge tone={i.status === 'registered' ? 'success' : 'neutral'}>
                      {t(`admin.inviteStatus.${i.status}`)}
                    </Badge>
                  </li>
                ))}
              </ul>
            )}
          </Card>

          <Card>
            <h2 className="text-sm font-bold">{t('admin.notes.title')}</h2>
            <TextAreaField
              label={t('admin.notes.label')}
              placeholder={t('admin.notes.placeholder')}
              maxLength={2000}
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
            <Button
              variant="secondary"
              size="md"
              className="w-auto! self-start"
              loading={addNote.isPending}
              disabled={!note.trim()}
              onClick={submitNote}
            >
              {t('admin.notes.add')}
            </Button>
            {addNote.isError && (
              <p role="alert" className="text-sm text-danger">
                {t('admin.notes.error')}
              </p>
            )}
            {r.notes.length === 0 ? (
              <p className="text-sm text-ink-muted">{t('admin.notes.none')}</p>
            ) : (
              <ul className="space-y-3">
                {r.notes.map((n) => (
                  <li key={n.id} className="border-t border-line pt-3 text-sm">
                    <p className="whitespace-pre-wrap">{n.body}</p>
                    <p className="text-helper text-ink-muted">
                      {t('admin.notes.by', {
                        email: n.author.email,
                        date: formatDateTime(n.createdAt),
                      })}
                    </p>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>
      </div>
    </section>
  );
}
